import { Injectable } from '@angular/core';
import { catchError, map, Observable, of, shareReplay } from 'rxjs';
import { BipTemplateService } from '../../features/bip-template/bip-template.service';
import {
  TemplateModel,
  TemplateSection,
} from '../../features/bip-template/bip-template.interface';

export enum BipTileSection {
  Details = 'Challenging Behavior',
  Assessment = 'Behavior Screener',
  Strategy = 'Strategy Details',
  Data = 'Reports',
}

const NON_QUESTIONNAIRE_SECTION_NAMES = new Set<string>([
  ...Object.values(BipTileSection),
  'Custom Questions',
]);

@Injectable({
  providedIn: 'root',
})
export class BipTemplateGatingService {
  private readonly templateCache = new Map<
    number,
    Observable<TemplateModel | null>
  >();

  constructor(private readonly bipTemplateService: BipTemplateService) {}

  getTemplate(bipTemplateId: number): Observable<TemplateModel | null> {
    if (!bipTemplateId) {
      return of(null);
    }
    if (!this.templateCache.has(bipTemplateId)) {
      const template$ = this.bipTemplateService
        .getTemplateById(bipTemplateId)
        .pipe(
          map(response => response.result),
          // A failed fetch must not poison the cache forever — shareReplay(1)
          // would otherwise replay that same error to every future caller for
          // this id with no new HTTP request ever firing again. Drop the
          // cache entry so the next getTemplate() call retries fresh, and
          // resolve current subscribers to null (same fallback already used
          // for a falsy id).
          catchError(() => {
            this.templateCache.delete(bipTemplateId);
            return of(null);
          }),
          // refCount: true so concurrent callers for the same case-detail open
          // (e.g. view-bip-case + its behavior-list child) share one HTTP call.
          // Note: refCount only resets the buffer on unsubscribe for a source
          // that hasn't completed yet — an HTTP call completes immediately, so
          // this alone does NOT expire the cache. Call invalidate() after any
          // template save/reorder to avoid serving a stale response forever.
          shareReplay({ bufferSize: 1, refCount: true })
        );
      this.templateCache.set(bipTemplateId, template$);
    }
    return this.templateCache.get(
      bipTemplateId
    ) as Observable<TemplateModel | null>;
  }

  /**
   * Drops a cached template so the next getTemplate() call fetches fresh data.
   * Must be called after any successful save/reorder of the template (shareReplay
   * keeps a completed HTTP response cached indefinitely otherwise).
   */
  invalidate(bipTemplateId: number): void {
    this.templateCache.delete(bipTemplateId);
  }

  private findSection(
    template: TemplateModel | null,
    sectionName: string
  ): TemplateSection | undefined {
    return template?.templateSections?.find(
      section => section.sectionName === sectionName
    );
  }

  private getQuestionnaireSections(
    template: TemplateModel | null
  ): TemplateSection[] {
    return (template?.templateSections ?? []).filter(
      section => !NON_QUESTIONNAIRE_SECTION_NAMES.has(section.sectionName)
    );
  }

  /**
   * Whether a fixed behavior sub-tile (Details/Assessment/Strategy/Data) is included in the
   * assigned template.
   */
  isTileSectionIncluded(
    template: TemplateModel | null,
    tile: BipTileSection
  ): boolean {
    return this.findSection(template, tile)?.isSelected ?? false;
  }

  /**
   * Whether a fixed behavior sub-tile's section is flagged Hard-Stop on the template — used to
   * decide whether the *next* tile in the fixed sequence must wait for this one to be completed.
   */
  isTileSectionHardStop(
    template: TemplateModel | null,
    tile: BipTileSection
  ): boolean {
    return this.findSection(template, tile)?.isHardStop ?? false;
  }

  /**
   * Whether a fixed behavior sub-tile's section is flagged Required on the template — used to decide
   * whether the *next* tile in the fixed sequence must wait for this one to be completed. A section
   * flagged Hard-Stop is always also Required (auto-linked in the template builder), so callers that
   * need "must complete before continuing" should check `isTileSectionHardStop || isTileSectionRequired`.
   */
  isTileSectionRequired(
    template: TemplateModel | null,
    tile: BipTileSection
  ): boolean {
    return this.findSection(template, tile)?.isRequired ?? false;
  }

  /**
   * Help text configured on the template for a fixed tile section, or a named section such as
   * 'Questionnaires' — used to show the template author's guidance on that section's drawer title.
   * Returns null when no help text was added (nothing to show).
   */
  getSectionHelpText(
    template: TemplateModel | null,
    sectionName: BipTileSection | 'Questionnaires'
  ): string | null {
    const helpText = this.findSection(template, sectionName)?.helpText;
    return helpText?.trim() ? helpText.trim() : null;
  }

  /**
   * The next tile after `fromTile` (fixed Details -> Assessment -> Strategy -> Data order) that's
   * included on the template, or null if none of the remaining tiles are included. This is what a
   * "Continue to X" button should point to — it skips any tile the template excludes, so e.g. a
   * template with Strategy off and Data on resolves Assessment's next tile straight to Data.
   * Purely template-driven: completion state of any tile plays no part in this decision.
   */
  resolveNextIncludedTile(
    template: TemplateModel | null,
    fromTile: BipTileSection
  ): BipTileSection | null {
    const order = Object.values(BipTileSection);
    const remaining = order.slice(order.indexOf(fromTile) + 1);
    return (
      remaining.find(tile => this.isTileSectionIncluded(template, tile)) ?? null
    );
  }

  hasIncludedQuestionnaire(template: TemplateModel | null): boolean {
    return this.getQuestionnaireSections(template).some(
      section => section.isSelected
    );
  }

  /**
   * True when the template has an included Hard-Stop questionnaire. Real completion status isn't
   * tracked yet (pending the plan-questionnaire-attachment API), so presence alone gates Add Behavior
   * for now — this will need to become a real completion check once that API lands.
   */
  hasIncludedHardStopQuestionnaire(template: TemplateModel | null): boolean {
    return this.getQuestionnaireSections(template).some(
      section => section.isSelected && section.isHardStop
    );
  }
}
